import React from 'react';
import { Link } from 'react-router-dom';
import { Palette, Eye, Building, Crown, ArrowRight } from 'lucide-react';
import '../styles/DashboardDemo.css';

const DashboardDemo = () => {
  const dashboards = [
    {
      title: 'Artist Studio',
      description: 'Manage your artworks and portfolio',
      href: '/artist/dashboard',
      icon: Palette,
      type: 'artist',
      color: '#6f42c1'
    },
    {
      title: 'Collector Hub',
      description: 'Discover and collect art',
      href: '/collector/dashboard',
      icon: Eye,
      type: 'collector',
      color: '#28a745'
    },
    {
      title: 'Gallery Admin',
      description: 'Manage gallery operations',
      href: '/gallery/dashboard',
      icon: Building,
      type: 'gallery',
      color: '#007bff'
    },
    {
      title: 'Curator Dashboard',
      description: 'Curate galleries, collections, and events',
      href: '/curator/collections',
      icon: Building,
      type: 'curator',
      color: '#fd7e14'
    },
    {
      title: 'Admin Dashboard',
      description: 'Full platform administration',
      href: '/admin/dashboard',
      icon: Crown,
      type: 'admin',
      color: '#dc3545'
    }
  ];

  return (
    <div className="dashboard-demo">
      <div className="demo-container">
        <div className="demo-header">
          <h1 className="demo-title">🎨 Unified Dashboard Layout System</h1>
          <p className="demo-subtitle">
            A consistent, responsive design system for all VibrantCast dashboards
          </p>
        </div>

        <div className="demo-features">
          <h2>✨ Key Features</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🎯</div>
              <h3>Unified Design</h3>
              <p>Consistent layout, colors, and interactions across all dashboards</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📱</div>
              <h3>Responsive</h3>
              <p>Mobile-first design with clean side menu collapse</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔄</div>
              <h3>Dashboard Switching</h3>
              <p>Quick access to other dashboards via avatar dropdown</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎨</div>
              <h3>Customizable</h3>
              <p>Different navigation menus and branding for each dashboard type</p>
            </div>
          </div>
        </div>

        <div className="demo-dashboards">
          <h2>🚀 Available Dashboards</h2>
          <div className="dashboards-grid">
            {dashboards.map((dashboard) => (
              <div key={dashboard.type} className="dashboard-demo-card" style={{ borderTopColor: dashboard.color }}>
                <div className="dashboard-demo-icon" style={{ color: dashboard.color }}>
                  <dashboard.icon size={32} />
                </div>
                <h3 className="dashboard-demo-title">{dashboard.title}</h3>
                <p className="dashboard-demo-description">{dashboard.description}</p>
                <Link to={dashboard.href} className="dashboard-demo-button">
                  Try Dashboard
                  <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>

        <div className="demo-layout">
          <h2>🏗️ Layout Structure</h2>
          <div className="layout-diagram">
            <div className="layout-header">
              <div className="layout-section">
                <h4>Top Header</h4>
                <ul>
                  <li>Sidebar toggle button</li>
                  <li>Dashboard logo & title</li>
                  <li>Notifications</li>
                  <li>User avatar with dropdown</li>
                </ul>
              </div>
            </div>
            <div className="layout-body">
              <div className="layout-sidebar">
                <div className="layout-section">
                  <h4>Sidebar</h4>
                  <ul>
                    <li>Navigation menu</li>
                    <li>Active state indicators</li>
                    <li>Settings & Sign Out at bottom</li>
                  </ul>
                </div>
              </div>
              <div className="layout-main">
                <div className="layout-section">
                  <h4>Main Content</h4>
                  <ul>
                    <li>Dashboard-specific content</li>
                    <li>Responsive padding & spacing</li>
                    <li>Scrollable content area</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="demo-responsive">
          <h2>📱 Responsive Behavior</h2>
          <div className="responsive-breakpoints">
            <div className="breakpoint">
              <h4>Desktop (1024px+)</h4>
              <p>Full sidebar visible, header with all elements</p>
            </div>
            <div className="breakpoint">
              <h4>Tablet (768px - 1023px)</h4>
              <p>Collapsible sidebar, simplified header</p>
            </div>
            <div className="breakpoint">
              <h4>Mobile (480px - 767px)</h4>
              <p>Mobile menu overlay, compact header</p>
            </div>
            <div className="breakpoint">
              <h4>Small Mobile (&lt; 480px)</h4>
              <p>Optimized spacing, touch-friendly controls</p>
            </div>
          </div>
        </div>

        <div className="demo-cta">
          <h2>🎯 Ready to Implement?</h2>
          <p>Choose a dashboard above to see the unified layout in action!</p>
          <div className="cta-buttons">
            <Link to="/dashboard-selector" className="cta-button primary">
              View All Dashboards
            </Link>
            <Link to="/" className="cta-button secondary">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardDemo;
